import { useState, useEffect } from 'react'
import styles from './AddMemberToGroupModal.module.css'
import apiClient from '../../utils/api'
import useEscapeKey from '../../hooks/useEscapeKey'

export default function AddMemberToGroupModal({ 
    isOpen, 
    onClose, 
    onSave,
    member,
    availableGroups = [],
    memberGroups = [],
    apiToken = null
}) {
    const [selectedGroupId, setSelectedGroupId] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    // Add escape key functionality
    useEscapeKey(isOpen, onClose, !loading)

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setSelectedGroupId('')
            setError('')
        }
    }, [isOpen])

    // Filter groups that the member is not already part of
    const availableGroupsToJoin = availableGroups.filter(group => 
        !memberGroups.some(memberGroup => memberGroup.id === group.id)
    )

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!selectedGroupId) {
            setError('Please select a group')
            return
        }

        if (!member) {
            setError('Member information is missing')
            return
        }

        setLoading(true)
        setError('')

        try {
            if (!apiToken) {
                throw new Error('Authentication token is required')
            }

            apiClient.setAuthToken(apiToken)
            
            const response = await apiClient.addMemberToGroup(selectedGroupId, member.id)
            
            // Notify parent component of successful addition
            if (onSave) {
                const selectedGroup = availableGroups.find(g => g.id === selectedGroupId)
                onSave(selectedGroup)
            }
            
            onClose()
        } catch (err) {
            console.error('Failed to add member to group:', err)
            setError(err.message || 'Failed to add member to group. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        if (!loading) {
            onClose()
        }
    }

    if (!isOpen) return null

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>Add User to Group</h2>
                    <button 
                        className={styles.closeButton}
                        onClick={handleCancel}
                        aria-label="Close modal"
                        disabled={loading}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    {/* User Information Display */}
                    {member && (
                        <div className={styles.memberInfo}>
                            <div className={styles.memberAvatar}>
                                {member.avatar_url ? (
                                    <img src={member.avatar_url} alt={member.full_name} />
                                ) : (
                                    <div className={styles.avatarPlaceholder}>
                                        {member.first_name?.charAt(0)?.toUpperCase() || ''}
                                        {member.last_name?.charAt(0)?.toUpperCase() || ''}
                                    </div>
                                )}
                            </div>
                            <div className={styles.memberDetails}>
                                <h3>{member.full_name || member.display_name}</h3>
                                <p>{member.email}</p>
                            </div>
                        </div>
                    )}

                    {/* Group Selection */}
                    <div className={styles.formGroup}>
                        <label htmlFor="groupSelect" className={styles.label}>
                            Select Group *
                        </label>
                        {availableGroupsToJoin.length > 0 ? (
                            <select
                                id="groupSelect"
                                className={styles.select}
                                value={selectedGroupId}
                                onChange={(e) => setSelectedGroupId(e.target.value)}
                                disabled={loading}
                                required
                            >
                                <option value="">Choose a group...</option>
                                {availableGroupsToJoin.map(group => (
                                    <option key={group.id} value={group.id}>
                                        {group.name}
                                        {group.description && ` - ${group.description}`}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <div className={styles.noGroupsMessage}>
                                <p>This member is already part of all available groups, or there are no groups to join.</p>
                                <p>Create a new group first if needed.</p>
                            </div>
                        )}
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className={styles.errorMessage}>
                            {error}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className={styles.buttonRow}>
                        <div className={styles.rightButtons}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={styles.saveButton}
                                disabled={loading || availableGroupsToJoin.length === 0}
                            >
                                {loading ? 'Adding...' : 'Add to Group'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}